import { Box, Typography } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import React from 'react';

import { T } from '../../theme/tokens';
import { SynButton } from '../ds';

/**
 * Error boundary around the lazy-loaded panel area.
 *
 * All screen panels are code-split (React.lazy). After a new release the old
 * chunk filenames disappear from the server, so a visitor with a stale page
 * open who clicks into a not-yet-visited screen gets a failed dynamic import
 * and React.lazy throws. Without this boundary the whole panel area would
 * crash; with it we keep the shell + nav intact and show a friendly
 * "new version available — reload" view.
 *
 * `resetKey` should change whenever the user navigates (active panel index /
 * portal toggle) so a one-off failure doesn't permanently lock the panel area:
 * navigating elsewhere clears the error and re-attempts rendering.
 */

interface Props {
    resetKey: string | number;
    children: React.ReactNode;
}

interface State {
    hasError: boolean;
    isChunkError: boolean;
    lastResetKey: string | number;
}

/**
 * True when the error looks like a failed code-split chunk download (webpack
 * ChunkLoadError / dynamic-import fetch failure) rather than a genuine render
 * bug. Non-chunk errors get honest generic messaging instead of the
 * "new version available" copy, so real panel bugs aren't masked.
 */
export function isChunkLoadError(error: unknown): boolean {
    if (!(error instanceof Error)) return false;
    const text = `${error.name} ${error.message}`;
    return /ChunkLoadError|Loading chunk [^\s]+ failed|Loading CSS chunk|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed/i.test(text);
}

export default class PanelChunkErrorBoundary extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        this.state = { hasError: false, isChunkError: false, lastResetKey: props.resetKey };
    }

    static getDerivedStateFromError(error: unknown): Partial<State> {
        return { hasError: true, isChunkError: isChunkLoadError(error) };
    }

    static getDerivedStateFromProps(props: Props, state: State): Partial<State> | null {
        // Navigating to a different panel clears the error and retries the
        // (possibly different) chunk import.
        if (props.resetKey !== state.lastResetKey) {
            return { hasError: false, isChunkError: false, lastResetKey: props.resetKey };
        }
        return null;
    }

    componentDidCatch(error: Error): void {
        // eslint-disable-next-line no-console
        console.error(
            this.state.isChunkError
                ? 'Panel chunk failed to load (likely a stale page after a release):'
                : 'Panel crashed while rendering:',
            error,
        );
    }

    handleReload = (): void => {
        window.location.reload();
    };

    render(): React.ReactNode {
        if (this.state.hasError) {
            return (
                <Box
                    data-testid="panel-chunk-error"
                    sx={{
                        display: 'flex', flexDirection: 'column', alignItems: 'center',
                        pt: 10, px: 3, textAlign: 'center', gap: 1.5,
                    }}
                >
                    <Typography sx={{ fontSize: '1.05rem', fontWeight: 600, color: T.color.text.primary }}>
                        {this.state.isChunkError
                            ? 'A new version of Synterra Health is available'
                            : 'This screen ran into a problem'}
                    </Typography>
                    <Typography sx={{ fontSize: '0.85rem', color: T.color.text.muted, maxWidth: 420 }}>
                        {this.state.isChunkError
                            ? 'This screen couldn\u2019t load because the app was updated since this page was opened. Reload to pick up the latest version — your work on other screens is unaffected.'
                            : 'Something went wrong while showing this screen. Reloading usually fixes it — your work on other screens is unaffected.'}
                    </Typography>
                    <SynButton
                        variant="primary"
                        startIcon={<RefreshIcon />}
                        onClick={this.handleReload}
                        sx={{ mt: 1 }}
                    >
                        Reload to continue
                    </SynButton>
                </Box>
            );
        }
        return this.props.children;
    }
}
